import Splide, { ComponentConstructor, Components } from '@splidejs/splide';

import { LiveRegion } from './live-region';

type GalleryBlockSplideExtensionFactory = (
    galleryBlock: HTMLElement
) => ComponentConstructor;
const GalleryBlockSplideExtensionFactory: GalleryBlockSplideExtensionFactory =
    (galleryBlock: HTMLElement): ComponentConstructor =>
    (Splide: Splide, Components: Components) => {
        const { slides, root } = Components.Elements;
        const counterElement: HTMLElement = galleryBlock.querySelector(
            '[data-gallery-block-counter]'
        );
        const captionElement: HTMLElement = galleryBlock.querySelector(
            '[data-gallery-block-caption]'
        );
        const captions: Array<string> = Array.prototype.slice
            .call(
                galleryBlock.querySelectorAll(
                    '[data-gallery-block-caption-content]'
                )
            )
            .map((item: HTMLElement) => item.innerHTML || '');
        const liveRegion: LiveRegion = new LiveRegion();

        Splide.on('destroy', () => {
            liveRegion.destroy();
        });

        function mount() {
            if (!slides || !slides.length) {
                return;
            }
            if (slides.length === 1) {
                Splide.options = {
                    ...Splide.options,
                    keyboard: false,
                    drag: false,
                };
                root.setAttribute('tabindex', '-1');
            }
            liveRegion.appendTo(root);
            update(true);
            Splide.on('move', update);
        }

        function update(isInitializing = false) {
            const activeIndex = Splide.index;
            const activeSlide: HTMLElement = slides[activeIndex];
            if (!activeSlide) {
                return;
            }
            const counterText: string = `${activeIndex + 1}/${slides.length}`;
            if (counterElement) {
                counterElement.textContent = counterText;
            }
            let captionText: string = captions[activeIndex] || '';
            if (captionElement) {
                const div = document.createElement('div');
                div.classList.add('gallery-block__caption-content');
                div.innerHTML = captionText;
                captionText = div.textContent.trim();
                captionElement.innerHTML = '';
                captionElement.appendChild(div);
                requestAnimationFrame(() => {
                    captionElement.style.height = `${
                        captionText ? div.offsetHeight : 0
                    }px`;
                });
            }
            if (!isInitializing) {
                liveRegion.announce(
                    `Slide ${counterText}, caption: ${captionText}`
                );
            }
        }

        return {
            mount,
        };
    };

export class Gallery {
    private galleryBlock: HTMLElement;

    constructor(galleryBlock: HTMLElement) {
        this.galleryBlock = galleryBlock;
        this.initialize();
    }

    initialize() {
        const sliderElement = this.galleryBlock.querySelector(
            '[data-gallery-block-slider]'
        ) as HTMLElement;
        if (!sliderElement) {
            return;
        }
        const GalleryBlockSplideExtension: ComponentConstructor =
            GalleryBlockSplideExtensionFactory(this.galleryBlock);
        new Splide(sliderElement, {
            autoWidth: true,
            type: 'loop',
            pagination: false,
            focus: 'center',
            lazyLoad: 'nearby',
            preloadPages: 2,
            classes: {
                arrow: 'splide__arrow btn',
            },
            keyboard: 'focused',
            drag: true,
            wheel: false,
            slideFocus: false,
        }).mount({
            GalleryBlockSplideExtension,
        });
    }
}
